import {
  MasonryEngineActionHandler,
  FieldNodeStateWithId,
  MasonryEngineStateController,
  MasonryEngineMessagingController,
} from '@leagueplatform/masonry-engine';
import {
  MasonryDriverAction,
  MasonryValidateFlowStepAction,
  MasonryFlowNode,
} from '@leagueplatform/masonry-renderers';
import {
  getVisibleFormFieldNodes,
  validateAndUpdateFieldNodes,
} from '../utils/form-validation-utils';

type FilterFieldsToCurrentStepParams = {
  allFieldNodes: FieldNodeStateWithId[];
  formId: string;
  stateController: MasonryEngineStateController;
  messagingController: MasonryEngineMessagingController;
};

/**
 * Helper function to filter field nodes to only include those from the current step
 * This logic is extracted from submit-flow-step.ts for reusability
 */
export const filterFieldsToCurrentStep = ({
  allFieldNodes,
  formId,
  stateController,
  messagingController,
}: FilterFieldsToCurrentStepParams): FieldNodeStateWithId[] => {
  // Get the form node state to find the current step
  const flowNodeState = stateController.getNodeState<MasonryFlowNode>(formId);
  if (!flowNodeState) {
    throw new Error(`Flow node state not found for id ${formId}`);
  }

  // Get the ancestry of each field node
  const formFieldNodeAncestries = allFieldNodes.map(({ nodeId }) =>
    messagingController.sendMessage(nodeId, {
      type: 'getAncestry',
      payload: {},
    }),
  );

  const flowNodeData = messagingController.sendMessage(formId, {
    type: 'getData',
    payload: {},
  }) as MasonryFlowNode;

  // Get the raw node data of the nodes in this flow node's `steps` section
  const flowNodeSteps = flowNodeData.sections.steps;

  // The current step's node ID is the ID of the `steps` node at the `currentStepIndex`
  const currentStepNodeId =
    flowNodeSteps[flowNodeState.currentStepIndex || 0].id;

  // Filter every field node whose ancestry includes the current step node ID
  return allFieldNodes.filter((__, index) =>
    formFieldNodeAncestries[index]?.includes(currentStepNodeId),
  );
};

/**
 * Validates the current step of a flow form without submitting it.
 *
 * This action handler:
 * 1. Gets all visible form fields from the current step
 * 2. Validates the fields using the validation controller
 * 3. Updates field status messages for any validation errors
 * 4. Focuses the first invalid field if validation fails
 * 5. Returns a boolean promise indicating whether validation passed
 *
 * @example
 * // Use in a flow node action:
 * {
 *   type: 'validateFlowStep',
 *   namespace: 'default',
 *   payload: {
 *     formId: 'my-flow-form'
 *   }
 * }
 *
 * @returns Promise<boolean> - true if validation passes, false if validation fails
 */
export const ValidateFlowStepActionHandler: MasonryEngineActionHandler<
  MasonryValidateFlowStepAction,
  MasonryDriverAction
> = (
  action,
  { stateController, messagingController, validationController },
) => {
  const { payload } = action;
  const { formId } = payload;

  // Get all visible field nodes for the form
  const allFieldNodes = getVisibleFormFieldNodes(formId, stateController);

  // Filter to only include fields from the current step (similar to submit-flow-step logic)
  const fieldNodes = filterFieldsToCurrentStep({
    allFieldNodes,
    formId,
    stateController,
    messagingController,
  });

  // Validate fields and update status messages, focus first invalid field
  const isValid = validateAndUpdateFieldNodes(
    fieldNodes,
    validationController,
    stateController,
    messagingController,
  );

  if (!isValid) {
    return {
      isSuccess: false,
      error: 'Validation failed',
    };
  }

  return {
    isSuccess: true,
    data: isValid,
  };
};
